import { call, put, select } from 'redux-saga/effects';
import {
  gridPanelAvailableTabChanged,
  gridPanelOpened,
  selectAvailableTabs,
  selectLockedContent,
} from 'features/layout/features/content/contentSlice';
import {
  activeFilesCollectionUpdated,
  currentActiveFileUpdated,
  fileOpened,
  loaderDisplayChanged,
  previewFileClosed,
  selectActiveFile,
  selectPreviewedFile,
} from 'features/content-library/contentLibrarySlice';
import { signalingContentLibraryFileOpened } from 'features/content-library/actions';
import { ContentLibraryOpenedFile } from 'features/content-library/types';
import {
  type InitContentWhiteboardData,
  initContentWhiteboardDataSaga,
} from 'features/content-whiteboard/sagas/initContentWhiteboardDataSaga';
import { previewContentFileSaga } from 'features/content-library/sagas/previewContentFileSaga';
import { contentBoard } from 'features/content-whiteboard/utils/contentBoardStateManager';
import { ContentData, GridPanelName } from 'features/layout/features/content/types';
import { selectLocalUserId } from 'features/users/usersSlice';
import { UserId } from 'features/users/types';
import { openModal } from 'features/modal/modalSlice';

export function* onContentLibraryFileOpenedSaga(
  action: ReturnType<typeof signalingContentLibraryFileOpened>
) {
  if (action.payload.data.preview) {
    yield call(previewContentFileSaga, action);
    return;
  }

  const previewedFile: ContentLibraryOpenedFile | null = yield select(selectPreviewedFile);
  const lockedContent: ContentData = yield select(selectLockedContent);
  const localUserId: UserId = yield select(selectLocalUserId);

  const isOverridingLockedContent = lockedContent && lockedContent.id !== action.payload.data.id;
  if (isOverridingLockedContent && localUserId !== action.payload.initiator.id) {
    yield put(
      activeFilesCollectionUpdated({
        [action.payload.data.id]: action.payload.data,
      })
    );

    const availableTabs: Record<GridPanelName, boolean> = yield select(selectAvailableTabs);

    if (!availableTabs.contentLibrary) {
      yield put(gridPanelAvailableTabChanged({ type: 'contentLibrary', active: true }));
    }

    return;
  }

  if (!previewedFile || localUserId === action.payload.initiator.id) {
    yield put(loaderDisplayChanged(true));
  }

  const existingFile: ContentLibraryOpenedFile | undefined = yield select(
    selectActiveFile,
    action.payload.data.id
  );

  const fileData = {
    ...action.payload.data,
    page: existingFile?.page ?? action.payload.data.page,
  };

  yield put(fileOpened(fileData));
  yield put(currentActiveFileUpdated(action.payload.data.id));
  yield put(gridPanelOpened('contentLibrary'));

  if (!previewedFile || localUserId === action.payload.initiator.id) {
    yield call(contentBoard.clean);

    const { id, whiteboardShapes: shapes = [], name } = action.payload.data;

    const data: InitContentWhiteboardData = {
      id,
      page: fileData.page,
      name,
      shapes,
    };

    yield call(initContentWhiteboardDataSaga, data);
  }

  if (previewedFile && localUserId === action.payload.initiator.id) {
    yield put(previewFileClosed());
  }

  if (!previewedFile && isOverridingLockedContent && localUserId === action.payload.initiator.id) {
    yield put(openModal('pushLockedContentConfirm'));
  }
}
